/**
 * Dock Toolbar — Composition Root
 *
 * A floating island toolbar with expandable panel for workflow operations.
 * Composes: OperationPanel, DockBar, PrimaryActions.
 */

import React, { useState, useCallback, useRef, useEffect, Component, type ErrorInfo } from "react";
import { makeStyles, mergeClasses, tokens } from "@fluentui/react-components";
import { ReOrderDotsVertical20Regular } from "@fluentui/react-icons";
import { useStyles } from "./dockToolbar.styles";
import type {
  ErrorBoundaryState,
  ErrorBoundaryProps,
  DockToolbarProps,
} from "./dockToolbar.types";
import { DockBar } from "./DockBar";
import { PrimaryActions } from "./PrimaryActions";
import { OperationPanel } from "../OperationPanel/OperationPanel";
import { useToolbarShortcuts } from "./hooks/useToolbarShortcuts";
import { useToolbarDrag } from "./hooks/useToolbarDrag";

const useLocalStyles = makeStyles({
  errorFallback: {
    padding: tokens.spacingVerticalS,
    textAlign: 'center' as const,
    color: tokens.colorNeutralForeground3,
  },
});

const ErrorFallback: React.FC = () => {
  const styles = useLocalStyles();
  return (
    <div className={styles.errorFallback}>
      Toolbar unavailable
    </div>
  );
};

// Error Boundary for graceful error handling
class DockToolbarErrorBoundary extends Component<
  ErrorBoundaryProps,
  ErrorBoundaryState
> {
  constructor(props: ErrorBoundaryProps) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError(error: Error): ErrorBoundaryState {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    console.error("[DockToolbar] Error caught by boundary:", error, errorInfo);
  }

  render() {
    if (this.state.hasError) {
      return this.props.fallback ?? <ErrorFallback />;
    }
    return this.props.children;
  }
}

const DockToolbarInner = (props: DockToolbarProps) => {
  const styles = useStyles();
  const [isPanelExpanded, setIsPanelExpanded] = useState(false);

  const disableCommands = props.disableCommands ?? false;

  // Collapse the dock's own OperationPanel whenever an external picker
  // (right-side OperationPickerPanel, MCP, sub-workflow) is open so the
  // user only ever sees a single picker at a time.
  useEffect(() => {
    if (props.isExternalPickerOpen) {
      setIsPanelExpanded(false);
    }
  }, [props.isExternalPickerOpen]);

  useToolbarShortcuts({
    isPanelExpanded,
    setIsPanelExpanded,
    disableCommands,
  });

  const {
    toolbarRef,
    position,
    isDragging,
    onDragHandleMouseDown,
    onDragHandleDoubleClick,
    dragStyle,
  } = useToolbarDrag({ containerRef: props.containerRef });

  // Track whether we're animating back to default (for smooth reset)
  const [isResetting, setIsResetting] = useState(false);
  const resetTimerRef = useRef<ReturnType<typeof setTimeout>>();

  // Clean up timer on unmount
  useEffect(() => {
    return () => {
      if (resetTimerRef.current) clearTimeout(resetTimerRef.current);
    };
  }, []);

  const handleDoubleClick = useCallback(() => {
    setIsResetting(true);
    onDragHandleDoubleClick();
    // Remove the animation class after transition completes
    if (resetTimerRef.current) clearTimeout(resetTimerRef.current);
    resetTimerRef.current = setTimeout(() => setIsResetting(false), 300);
  }, [onDragHandleDoubleClick]);

  const handleTogglePanel = useCallback(() => {
    setIsPanelExpanded((prev) => !prev);
  }, []);

  const handleClosePanel = useCallback(() => {
    setIsPanelExpanded(false);
  }, []);

  const operationPanelProps = {
    isExpanded: isPanelExpanded,
    onClose: handleClosePanel,
    hasTrigger: props.hasTrigger ?? false,
    ...(props.onOperationSelect && { onOperationSelect: props.onOperationSelect }),
    ...(props.onBrowseActions && { onBrowseActions: props.onBrowseActions }),
    ...(props.onBrowseTriggers && { onBrowseTriggers: props.onBrowseTriggers }),
    ...(props.onBrowseMCP && { onBrowseMCP: props.onBrowseMCP }),
    ...(props.onBrowseSubworkflow && { onBrowseSubworkflow: props.onBrowseSubworkflow }),
    ...(props.onBrowseTemplates && { onBrowseTemplates: props.onBrowseTemplates }),
    ...(props.onAddNote && { onAddNote: props.onAddNote }),
  };

  const primaryActionsProps = {
    isPanelExpanded,
    onTogglePanel: handleTogglePanel,
    isCodeVisible: props.isCodeVisible,
    onToggleCode: props.onToggleCode,
    isConversational: props.isConversational ?? false,
    isChatOpen: props.isChatOpen,
    onToggleChat: props.onToggleChat,
    disableCommands,
    ...(props.onRun && { onRun: props.onRun }),
    ...(props.onStartChat && { onStartChat: props.onStartChat }),
  };

  return (
    <div
      ref={toolbarRef}
      className={mergeClasses(
        styles.wrapper,
        isDragging && styles.wrapperDragging,
        isResetting && !position && styles.wrapperAnimateReset,
      )}
      style={dragStyle}
    >
      <OperationPanel {...operationPanelProps} />
      <DockBar>
        <button
          type="button"
          className={mergeClasses(
            styles.dragHandle,
            isDragging && styles.dragHandleDragging,
          )}
          onMouseDown={onDragHandleMouseDown}
          onDoubleClick={handleDoubleClick}
          aria-label="Drag to reposition toolbar. Double-click to reset."
          title="Drag to move toolbar"
        >
          <ReOrderDotsVertical20Regular />
        </button>
        <div className={styles.islandDivider} />
        <PrimaryActions {...primaryActionsProps} />
      </DockBar>
    </div>
  );
};

// Export wrapped component with error boundary
export const DockToolbar: React.FC<DockToolbarProps> = (props) => (
  <DockToolbarErrorBoundary>
    <DockToolbarInner {...props} />
  </DockToolbarErrorBoundary>
);

export default DockToolbar;
